import { useState, useEffect } from 'react'
import { useAppLockStore } from '@/stores/app-lock.store'
import { appLockService } from '@/lib/app-lock'
import { Logo } from '@/components/common/Logo'

type Step = 'enter' | 'confirm' | 'biometric'

interface PinSetupProps {
  onComplete: () => void
}

export function PinSetup({ onComplete }: PinSetupProps) {
  const [step, setStep] = useState<Step>('enter')
  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [error, setError] = useState('')

  const {
    enableAppLock,
    biometricAvailable,
    biometryType,
    setBiometricEnabled,
  } = useAppLockStore()

  const currentPin = step === 'enter' ? pin : confirmPin
  const setCurrentPin = step === 'enter' ? setPin : setConfirmPin

  // Keyboard support for typing PIN
  useEffect(() => {
    if (step === 'biometric') return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handlePinInput(e.key)
      } else if (e.key === 'Backspace') {
        handleBackspace()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentPin, step])

  const handlePinInput = (digit: string) => {
    if (currentPin.length < 4) {
      const newPin = currentPin + digit
      setCurrentPin(newPin)
      setError('')

      if (newPin.length === 4) {
        if (step === 'enter') {
          setStep('confirm')
        } else if (step === 'confirm') {
          handleConfirm(newPin)
        }
      }
    }
  }

  const handleBackspace = () => {
    setCurrentPin(currentPin.slice(0, -1))
    setError('')
  }

  const handleConfirm = async (enteredConfirmPin: string) => {
    if (enteredConfirmPin !== pin) {
      setError('PINs do not match. Try again.')
      setConfirmPin('')
      setStep('enter')
      setPin('')
      return
    }

    // PINs match - check if biometric is available
    if (biometricAvailable) {
      setStep('biometric')
    } else {
      await finishSetup(false)
    }
  }

  const finishSetup = async (enableBiometric: boolean) => {
    try {
      await enableAppLock(pin)
      if (enableBiometric) {
        await setBiometricEnabled(true)
      }
      onComplete()
    } catch (err) {
      console.error('PIN setup error:', err)
      setError('Failed to set up PIN. Please try again.')
      setStep('enter')
      setPin('')
      setConfirmPin('')
    }
  }

  const biometryName = appLockService.getBiometryTypeName(biometryType)

  if (step === 'biometric') {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white pt-safe pb-safe">
        <div className="flex flex-col items-center w-full max-w-xs px-6 text-center">
          <div className="mb-8">
            <Logo size="lg" />
          </div>

          <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
            </svg>
          </div>

          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Enable {biometryName}?
          </h1>
          <p className="text-gray-600 mb-8">
            Unlock Brightseed faster using {biometryName}. You can always use your PIN as a backup.
          </p>

          <button
            type="button"
            onClick={() => finishSetup(true)}
            className="w-full py-3 px-4 rounded-lg bg-primary-600 text-white font-medium mb-3 active:bg-primary-700"
          >
            Enable {biometryName}
          </button>

          <button
            type="button"
            onClick={() => finishSetup(false)}
            className="w-full py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium active:bg-gray-50"
          >
            Skip for now
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white pt-safe pb-safe">
      <div className="flex flex-col items-center w-full max-w-xs px-6">
        {/* Logo */}
        <div className="mb-8">
          <Logo size="lg" />
        </div>

        {/* Title */}
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          {step === 'enter' ? 'Create a PIN' : 'Confirm your PIN'}
        </h1>
        <p className="text-gray-600 text-center mb-6">
          {step === 'enter'
            ? 'Set a 4-digit PIN to secure your account'
            : 'Enter your PIN again to confirm'}
        </p>

        {/* Error message */}
        {error && (
          <p className="text-sm text-red-600 mb-4 text-center">{error}</p>
        )}

        {/* PIN dots */}
        <div className="flex gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 ${
                i < currentPin.length
                  ? 'bg-primary-600 border-primary-600'
                  : 'border-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => handlePinInput(num.toString())}
              className="w-16 h-16 rounded-full bg-gray-100 text-2xl font-semibold text-gray-900 active:bg-gray-200"
            >
              {num}
            </button>
          ))}
          <div /> {/* Empty cell */}
          <button
            type="button"
            onClick={() => handlePinInput('0')}
            className="w-16 h-16 rounded-full bg-gray-100 text-2xl font-semibold text-gray-900 active:bg-gray-200"
          >
            0
          </button>
          <button
            type="button"
            onClick={handleBackspace}
            disabled={currentPin.length === 0}
            className="w-16 h-16 rounded-full bg-gray-100 text-xl text-gray-600 active:bg-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z" />
            </svg>
          </button>
        </div>

        {/* Back button (only on confirm step) */}
        {step === 'confirm' && (
          <button
            type="button"
            onClick={() => {
              setStep('enter')
              setPin('')
              setConfirmPin('')
            }}
            className="text-gray-500 text-sm"
          >
            Start over
          </button>
        )}
      </div>
    </div>
  )
}
